// Sentry initialization for the GrantFlow frontend.
// Reads REACT_APP_SENTRY_DSN at build time. No-op if absent so local dev
// and unconfigured previews don't ship noise.

import * as Sentry from "@sentry/react";

const SENSITIVE_PARTS = ["password", "passwd", "pwd", "secret", "token", "apikey", "api_key", "email"];

function isSensitive(key) {
  const k = String(key).toLowerCase();
  return SENSITIVE_PARTS.some((p) => k.includes(p));
}

function scrub(value) {
  if (Array.isArray(value)) return value.map(scrub);
  if (value && typeof value === "object") {
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      out[k] = isSensitive(k) ? "[REDACTED]" : scrub(v);
    }
    return out;
  }
  return value;
}

// Errors originating from third-party in-app browsers (Facebook / Instagram /
// Threads / TikTok WebViews) that inject their own instrumentation JS. When the
// WebView is torn down their native Java bridge dies and throws — not our code.
const IAB_NOISE_PATTERNS = [
  /Java object is gone/i,
  /Error invoking postMessage/i,
  /navigation_performance_logger/i,
  /sendDataToNative/i,
  /sendJsBlockingTimeMessage/i,
  /iabjs:\/\//i,
];

function isIabNoise(event) {
  const ex = event?.exception?.values?.[0] || {};
  const msg = ex.value || event?.message || "";
  if (IAB_NOISE_PATTERNS.some((re) => re.test(msg))) return true;
  const frames = ex?.stacktrace?.frames || [];
  for (const f of frames) {
    const fn = f?.filename || "";
    if (/iabjs:\/\//i.test(fn) || /navigation_performance_logger/i.test(fn)) {
      return true;
    }
  }
  return false;
}

function beforeSend(event) {
  // Drop events from localhost / 127.0.0.1 — that's local dev noise, not real users.
  const url = event?.request?.url || "";
  if (/^https?:\/\/(localhost|127\.0\.0\.1)/i.test(url)) {
    return null;
  }
  // Drop Facebook / Instagram / Threads in-app browser injected-JS crashes.
  if (isIabNoise(event)) {
    return null;
  }
  // Drop bare AxiosError "Network Error" — these fire on every wifi flake / tab
  // suspend and are not actionable. Real backend issues surface elsewhere.
  const ex = event?.exception?.values?.[0];
  if (ex && ex.type === "AxiosError" && /network error/i.test(ex.value || "")) {
    return null;
  }
  for (const section of ["user", "request", "extra", "contexts", "tags"]) {
    if (event[section] && typeof event[section] === "object") {
      event[section] = scrub(event[section]);
    }
  }
  return event;
}

const dsn = process.env.REACT_APP_SENTRY_DSN;
const environment =
  process.env.REACT_APP_SENTRY_ENV ||
  (process.env.NODE_ENV === "production" ? "production" : "development");

let initialized = false;
if (dsn) {
  Sentry.init({
    dsn,
    environment,
    release: process.env.REACT_APP_SENTRY_RELEASE,
    sendDefaultPii: false,
    tracesSampleRate: parseFloat(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || "0"),
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    // Drop errors originating from third-party in-app browser injected scripts
    // (Facebook / Instagram / Threads / TikTok WebViews). These have their own
    // iabjs:// URL scheme that isn't from our code.
    denyUrls: [
      /^iabjs:\/\//i,
      /navigation_performance_logger/i,
    ],
    beforeSend,
  });
  initialized = true;
}

// Exported for unit tests. Not intended for app runtime use.
export const __test__ = { beforeSend, isIabNoise, IAB_NOISE_PATTERNS };

export { Sentry, initialized as sentryInitialized };
